// S30 AID 異常與稽核(/aid/anomalies)— 以 ca-manager 為主。池水位(各 type
// available/allocated/paired,低水位告警)+ 完整性異常(material_missing)。
// 後端 = restful 代理 /v3/admin/{stock,anomalies} → ca-manager(未落地 → 誠實缺口)。
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { ErrorState, LoadingState } from '../../components/StateBlock';
import { getStock, getAnomalies, type TypeStock, type Anomaly } from './aidSupplyApi';
import '../_shared/shared.css';
import './aid.css';

function isPendingBackend(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}
function pct(n: number, d: number): number {
  return d > 0 ? Math.min(100, Math.round((n / d) * 100)) : 0;
}

export default function AidAnomaliesPage() {
  const [stock, setStock] = useState<TypeStock[]>([]);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [low, setLow] = useState(0); // 低水位告警門檻(available < low)

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, a] = await Promise.all([getStock(), getAnomalies()]);
      setStock(s);
      setAnomalies(a);
    } catch (e) {
      setErr(e);
      setStock([]);
      setAnomalies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="aid-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">AID 異常與稽核</h1>
          <p className="page-subtitle">
            池水位(各 AID 類型可用量)與完整性異常。後端 = ca-manager(<span className="adm-mono">/v3/admin/stock</span>、<span className="adm-mono">/anomalies</span>)。
          </p>
        </div>
        <div className="aid-filter-row">
          <label className="adm-field">
            <span className="adm-field-label text-secondary">低水位告警門檻</span>
            <input type="number" min={0} value={low} onChange={(e) => setLow(Number(e.target.value) || 0)} style={{ width: 90 }} />
          </label>
          <button className="btn-ghost btn-sm" onClick={() => void load()} disabled={loading}>重新整理</button>
        </div>
      </div>

      {err && isPendingBackend(err) ? (
        <p className="aid-gap-note">
          <strong>誠實缺口:</strong>後端 <span className="adm-mono">/v3/admin/stock</span> / <span className="adm-mono">/anomalies</span>(→ ca-manager)尚未落地。
        </p>
      ) : err ? (
        <ErrorState error={err} onRetry={() => void load()} />
      ) : loading ? (
        <LoadingState />
      ) : (
        <div className="aid-split">
          <section className="card">
            <div className="section-head">
              <div>
                <h2 className="section-title">池水位</h2>
                <span className="section-meta">各 AID 類型的可用／已配給／已配對;available &lt; 門檻 → 告警</span>
              </div>
            </div>
            {stock.length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>AID 類型</th><th>可用</th><th>已配給</th><th>已配對</th><th>池水位</th></tr>
                  </thead>
                  <tbody>
                    {stock.map((s) => {
                      const total = s.available + s.allocated + s.paired;
                      const alert = low > 0 && s.available < low;
                      return (
                        <tr key={s.type} className={alert ? 'is-warn' : ''}>
                          <td className="adm-mono">{s.type}</td>
                          <td className="adm-mono">{s.available}{alert && <span className="pill pill-warn" style={{ marginLeft: 6 }}>低水位</span>}</td>
                          <td className="adm-mono">{s.allocated}</td>
                          <td className="adm-mono">{s.paired}</td>
                          <td>
                            <div className="aid-bar" title={`可用 ${s.available} / 總 ${total}`}>
                              <div className="aid-bar-alloc" style={{ width: `${pct(s.available, total)}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state"><div className="empty-state-icon">📊</div><div className="empty-state-title">池為空</div></div>
            )}
          </section>

          <aside className="aid-side">
            <section className="card">
              <div className="section-head">
                <div>
                  <h2 className="section-title">異常</h2>
                  <span className="section-meta">material_missing:有 device_cert 無私鑰材料</span>
                </div>
                <span className={`pill ${anomalies.length > 0 ? 'pill-warn' : 'pill-good'}`}>{anomalies.length}</span>
              </div>
              {anomalies.length > 0 ? (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>異常類型</th><th>DID</th><th>說明</th></tr></thead>
                    <tbody>
                      {anomalies.map((a, i) => (
                        <tr key={a.did ?? i}>
                          <td><span className="pill pill-warn">{a.kind}</span></td>
                          <td className="adm-mono">{a.did ?? '—'}</td>
                          <td>{a.detail ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="aid-hint">無異常。</p>
              )}
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}
